import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import sonicProcessorUrl from '@/utils/sonic/sonic-processor.js?url';
import sonicWasmUrl from '@/utils/sonic/sonic.wasm?url';
import { DEFERRED_PLAYBACK_PRIMER_SRC } from '@/utils/deferredPlaybackPrimer';

export const MIN_PLAYBACK_RATE = 0.75;
export const MAX_PLAYBACK_RATE = 2;
export const PLAYBACK_RATE_STEP = 0.05;
export const DEFAULT_PLAYBACK_RATE = 1;

const STORAGE_KEY = 'aleph:playback-rate:v1';
// Below 1x the browser's own time-stretcher is the weak link, so that is where
// the Sonic worklet earns its keep. Above 1x we hand pitch back to the browser.
const RATE_EPSILON = 0.001;

/**
 * Human-readable speed label — "1x", "1.25x", "0.75x" (no trailing zeros).
 */
export const formatPlaybackRate = (rate: number): string =>
  `${Number(rate.toFixed(2))}x`;

/**
 * Snap to the slider's step grid and clamp into range. Rounding to two decimals
 * keeps float dust (0.7500000000000001) out of the label and out of the
 * `<input type="range">` value.
 */
export const normalizePlaybackRate = (value: number): number => {
  if (!Number.isFinite(value)) {
    return DEFAULT_PLAYBACK_RATE;
  }
  const snapped = Math.round(value / PLAYBACK_RATE_STEP) * PLAYBACK_RATE_STEP;
  const clamped = Math.min(MAX_PLAYBACK_RATE, Math.max(MIN_PLAYBACK_RATE, snapped));
  return Number(clamped.toFixed(2));
};

const readStoredRate = (): number => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw === null ? DEFAULT_PLAYBACK_RATE : normalizePlaybackRate(Number(raw));
  } catch {
    // Private mode / storage disabled — fall back to normal speed.
    return DEFAULT_PLAYBACK_RATE;
  }
};

const writeStoredRate = (rate: number): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(rate));
  } catch (error) {
    console.warn('Could not persist playback speed to localStorage.', error);
  }
};

/**
 * Sonic only has to run when slowing down; the browser preserves pitch well
 * enough when speeding up, and leaving it to do so keeps the worklet in
 * pass-through.
 */
const needsSonicPitchCorrection = (rate: number): boolean =>
  rate < DEFAULT_PLAYBACK_RATE - RATE_EPSILON;

/**
 * Push a rate onto the media element and, when present, onto the Sonic node.
 *
 * With no Sonic node (or above 1x) the browser owns pitch: `preservesPitch`
 * stays on. With Sonic engaged we deliberately turn `preservesPitch` off so the
 * element resamples — dropping the pitch by exactly the playback rate — and let
 * Sonic shift it back up by `1 / rate`. Sonic is doing pitch correction only;
 * the rate change itself is still the media element's.
 */
const applyPlaybackRate = (
  audio: HTMLAudioElement | null,
  sonicNode: AudioWorkletNode | null,
  rate: number,
): void => {
  const useNativePitchControl = !sonicNode || !needsSonicPitchCorrection(rate);

  if (audio) {
    audio.preservesPitch = useNativePitchControl;
    // Loading a new source resets playbackRate to defaultPlaybackRate, so set
    // both — chapter changes must not silently drop back to 1x.
    audio.defaultPlaybackRate = rate;
    audio.playbackRate = rate;
  }

  if (sonicNode) {
    sonicNode.parameters.get('pitch')!.value = useNativePitchControl ? 1 : 1 / rate;
  }
};

type AudioSourceKind = 'none' | 'primer' | 'same-origin' | 'cross-origin';

/**
 * A MediaElementAudioSourceNode built over a cross-origin resource without CORS
 * headers is tainted and outputs silence, so the Sonic graph is only safe for
 * same-origin audio. That rules it out on the Capacitor build (the page origin
 * is capacitor://localhost while audio comes from hi-aleph.com) and on any
 * CDN-served bundle — both fall back to native pitch preservation.
 *
 * The mobile deferred-playback primer is reported separately: it plays before
 * the real chapter URL is known, so it says nothing about the audio the graph
 * would end up carrying. Only http(s) sources can count as same-origin —
 * opaque "null" origins (data:, custom schemes, file://) could otherwise match
 * an opaque page origin.
 */
const classifyAudioSource = (audio: HTMLAudioElement): AudioSourceKind => {
  // src first: the player assigns sources directly, and currentSrc can still
  // describe the previous resource for a moment after a swap.
  const source = audio.src || audio.currentSrc;
  if (!source) return 'none';
  if (source === DEFERRED_PLAYBACK_PRIMER_SRC) return 'primer';
  try {
    const url = new URL(source, window.location.href);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return 'cross-origin';
    return url.origin === window.location.origin ? 'same-origin' : 'cross-origin';
  } catch {
    return 'cross-origin';
  }
};

export interface PlaybackSpeedState {
  playbackRate: number;
  setPlaybackRate: (rate: number) => void;
}

/**
 * Owns the audiobook's playback speed and the pitch correction that goes with
 * it.
 *
 * Word-level highlighting is unaffected by speed: the reader resolves the
 * active token from `audio.currentTime`, which advances in media time whatever
 * the rate, and Sonic changes pitch without changing duration.
 *
 * The Web Audio graph is built lazily, the first time a speed below 1x is
 * chosen. Until then the element plays straight to the output as it always has
 * — which is what keeps iOS background playback and the lock-screen session
 * untouched for everyone who never slows the narration down.
 */
export const usePlaybackSpeed = (
  audioRef: RefObject<HTMLAudioElement | null>,
): PlaybackSpeedState => {
  const [playbackRate, setPlaybackRateState] = useState<number>(readStoredRate);
  const playbackRateRef = useRef(playbackRate);

  const audioContextRef = useRef<AudioContext | null>(null);
  const sonicNodeRef = useRef<AudioWorkletNode | null>(null);
  const sourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null);
  // 'idle' until a sub-1x rate asks for Sonic; 'unavailable' is terminal and
  // means we stay on the browser's own pitch preservation for this session.
  const graphStatusRef = useRef<'idle' | 'starting' | 'ready' | 'unavailable'>('idle');
  // Bumped on every attempt so a message from an abandoned attempt's worklet
  // can't attach itself on top of a newer one.
  const graphGenerationRef = useRef(0);

  /** Drop back to the plain element path, keeping audio audible either way. */
  const abandonSonicGraph = useCallback((error?: unknown) => {
    if (graphStatusRef.current === 'unavailable') return;

    if (error) {
      console.warn('[usePlaybackSpeed] Falling back to native pitch preservation:', error);
    }

    const sonicNode = sonicNodeRef.current;
    sonicNodeRef.current = null;
    if (sonicNode) {
      try { sonicNode.disconnect(); } catch { /* already disconnected */ }
    }

    const context = audioContextRef.current;
    const sourceNode = sourceNodeRef.current;

    if (sourceNode) {
      // The element is already committed to the graph — a
      // MediaElementAudioSourceNode can never be undone — so the best we can do
      // is pass it through to the destination unprocessed. No way back from here.
      graphStatusRef.current = 'unavailable';
      if (context && context.state !== 'closed') {
        try { sourceNode.disconnect(); } catch { /* already disconnected */ }
        sourceNode.connect(context.destination);
      }
    } else {
      // Nothing was ever diverted, so the element is untouched and we can try
      // again on the next play or speed change.
      graphStatusRef.current = 'idle';
      audioContextRef.current = null;
      void context?.close().catch(() => { /* already closed */ });
    }

    applyPlaybackRate(audioRef.current, null, playbackRateRef.current);
  }, [audioRef]);

  const ensureSonicGraph = useCallback(() => {
    if (graphStatusRef.current !== 'idle') return;

    const audio = audioRef.current;
    const AudioContextConstructor =
      window.AudioContext ?? (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;

    if (!AudioContextConstructor || typeof AudioWorkletNode === 'undefined') {
      graphStatusRef.current = 'unavailable';
      return;
    }

    if (!audio) return;

    const sourceKind = classifyAudioSource(audio);
    // No audiobook loaded yet (nothing, or only the mobile primer) — stay
    // 'idle' so the real chapter's play retries rather than writing sonic off
    // for the session on a transient.
    if (sourceKind === 'none' || sourceKind === 'primer') return;

    if (sourceKind === 'cross-origin') {
      graphStatusRef.current = 'unavailable';
      return;
    }

    let context: AudioContext;
    try {
      context = new AudioContextConstructor();
    } catch (error) {
      abandonSonicGraph(error);
      return;
    }

    if (!context.audioWorklet?.addModule) {
      void context.close();
      graphStatusRef.current = 'unavailable';
      return;
    }

    graphStatusRef.current = 'starting';
    graphGenerationRef.current += 1;
    const generation = graphGenerationRef.current;
    audioContextRef.current = context;
    void context.resume().catch(() => { /* resumed again on the next play */ });

    void context.audioWorklet
      .addModule(sonicProcessorUrl)
      .then(() => fetch(sonicWasmUrl))
      .then((response) => {
        if (!response.ok) throw new Error(`Could not load sonic.wasm (${response.status})`);
        return response.arrayBuffer();
      })
      .then((wasmBinary) => {
        if (graphGenerationRef.current !== generation || graphStatusRef.current !== 'starting') return;

        const node = new AudioWorkletNode(context, 'sonic-processor', {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          outputChannelCount: [2],
        });

        node.port.onmessage = (event: MessageEvent) => {
          const message = event.data as { type?: string; message?: string };
          if (message?.type === 'ready') {
            if (graphGenerationRef.current !== generation || graphStatusRef.current !== 'starting') return;
            // Diverting the element is a one-way door: from here on its audio
            // only reaches the speakers through this graph. So do it only once
            // the worklet can pass audio through AND the context is actually
            // running — a suspended context would mean silence with no way back.
            void context.resume().then(() => {
              if (graphGenerationRef.current !== generation || graphStatusRef.current !== 'starting') return;
              if (context.state !== 'running') {
                throw new Error('AudioContext could not be resumed');
              }
              const audioElement = audioRef.current;
              if (!audioElement) {
                throw new Error('Audio element went away before sonic could attach');
              }
              // The source can change while the worklet loads (the primer
              // hands over to the real chapter, or a new book starts), so
              // check again right before the step that can't be undone.
              const attachSourceKind = classifyAudioSource(audioElement);
              if (attachSourceKind !== 'same-origin') {
                abandonSonicGraph();
                if (attachSourceKind === 'cross-origin') {
                  graphStatusRef.current = 'unavailable';
                }
                return;
              }
              const sourceNode = context.createMediaElementSource(audioElement);
              sourceNode.connect(node);
              sourceNodeRef.current = sourceNode;
              sonicNodeRef.current = node;
              graphStatusRef.current = 'ready';
              applyPlaybackRate(audioElement, node, playbackRateRef.current);
            }).catch(abandonSonicGraph);
          } else if (message?.type === 'error') {
            if (graphGenerationRef.current !== generation) return;
            abandonSonicGraph(new Error(message.message || 'Sonic processor initialization failed'));
          }
        };
        node.onprocessorerror = () => {
          if (graphGenerationRef.current !== generation) return;
          abandonSonicGraph(new Error('Sonic processor stopped unexpectedly'));
        };

        node.connect(context.destination);
        node.port.postMessage({ type: 'init', wasmBinary }, [wasmBinary]);
      })
      .catch(abandonSonicGraph);
  }, [abandonSonicGraph, audioRef]);

  const setPlaybackRate = useCallback((nextRate: number) => {
    const rate = normalizePlaybackRate(nextRate);
    playbackRateRef.current = rate;
    setPlaybackRateState(rate);
    writeStoredRate(rate);

    if (needsSonicPitchCorrection(rate)) {
      // Called from the slider, i.e. inside a user gesture — the best moment to
      // build the graph and get the context out of 'suspended'.
      ensureSonicGraph();
      void audioContextRef.current?.resume().catch(() => { /* retried on play */ });
    }

    applyPlaybackRate(audioRef.current, sonicNodeRef.current, rate);
  }, [audioRef, ensureSonicGraph]);

  // Keep the element on the chosen rate across chapter changes. Loading a new
  // source resets playbackRate, and Sonic's buffer has to be dropped on seeks
  // and source swaps or it replays audio from before the jump.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    applyPlaybackRate(audio, sonicNodeRef.current, playbackRateRef.current);

    const reapply = () => applyPlaybackRate(audio, sonicNodeRef.current, playbackRateRef.current);
    const resetSonicBuffer = () => sonicNodeRef.current?.port.postMessage({ type: 'reset' });
    const handlePlay = () => {
      const context = audioContextRef.current;
      if (context?.state === 'suspended') {
        void context.resume().catch(() => { /* nothing further we can do */ });
      } else if (!context && needsSonicPitchCorrection(playbackRateRef.current)) {
        // A slow rate was restored (or chosen) before any audio had loaded.
        ensureSonicGraph();
      }
      reapply();
    };

    audio.addEventListener('loadedmetadata', reapply);
    audio.addEventListener('canplay', reapply);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('seeking', resetSonicBuffer);
    audio.addEventListener('emptied', resetSonicBuffer);

    return () => {
      audio.removeEventListener('loadedmetadata', reapply);
      audio.removeEventListener('canplay', reapply);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('seeking', resetSonicBuffer);
      audio.removeEventListener('emptied', resetSonicBuffer);
    };
  }, [audioRef, ensureSonicGraph]);

  useEffect(() => () => {
    const context = audioContextRef.current;
    audioContextRef.current = null;
    sonicNodeRef.current = null;
    sourceNodeRef.current = null;
    void context?.close().catch(() => { /* already closed */ });
  }, []);

  return { playbackRate, setPlaybackRate };
};
